import { Group, CanvasTexture } from 'three';
import { partsMeshBuild } from './partsMeshBuild';
import { PartsMesh } from './PartsMesh';
import { PartsMeshs } from './PartsMeshs';
import { KintekiOrdermadeArrowModel } from './kintekiOrdermadeArrowModel';
import { EntekiOrdermadeArrowModel } from './entekiOrdermadeArrowModel';
import { MakiwaraOrdermadeArrowModel } from './makiwaraOrdermadeArrowModel';

type SetupCarving = () => {
    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D | null;
    tex: CanvasTexture;
};

const setupCarving: SetupCarving = () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 141;
    canvas.height = 225;

    const tex = new CanvasTexture(canvas);
    tex.name = 'carving.canvas';

    return { canvas, context, tex };
};

export const buildKinteki = (model: Group, enableRestore: boolean): KintekiOrdermadeArrowModel => {
    try {
        const shaft = partsMeshBuild(model, 'shaft', 'shaft', enableRestore) as PartsMeshs;
        const threads: PartsMeshs[] = [];
        [['ito1', 'thread'], ['ito2', 'thread'], ['ito3', 'thread']].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            threads.push(x);
        });
        const kebikis: PartsMeshs[] = [];
        [
            ['ke01', 'kebiki'],
            ['ke02', 'kebiki'],
            ['ke03', 'kebiki'],
            ['ke04', 'kebiki'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            kebikis.push(x);
        });

        // default show: hukumi-hazu
        const tenkyuHazu = partsMeshBuild(model, 'yahazu1', 'yahazu', enableRestore) as PartsMesh;
        const hukumiHazu = partsMeshBuild(model, 'yahazu2', 'yahazu', enableRestore) as PartsMesh;
        // default show: long-yajiri
        const shortYajiri = partsMeshBuild(model, 'yaziri1', 'yaziri', enableRestore) as PartsMesh;
        const longYajiri = partsMeshBuild(model, 'yaziri2', 'yaziri', enableRestore) as PartsMesh;
        const hazumakiFix = partsMeshBuild(model, 'hazumaki', 'hazumaki', enableRestore) as PartsMesh;
        const carving = partsMeshBuild(model, 'kokuin', 'kokuin', enableRestore) as PartsMesh;
        const hanaka = partsMeshBuild(model, 'hatyu', 'hatyu', enableRestore) as PartsMesh;

        const haneFront: PartsMeshs[] = [];
        [
            ['hane1', 'haneo'],
            ['hane2', 'haneo'],
            ['hane3', 'haneo'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            haneFront.push(x);
        });

        const haneBack: PartsMeshs[] = [];
        [
            ['hane1', 'haneu'],
            ['hane2', 'haneu'],
            ['hane3', 'haneu'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            haneBack.push(x);
        });

        const familycrest: PartsMesh[] = [];
        [
            ['kamon1', 'kamon'],
            ['kamon2', 'kamon'],
            ['kamon3', 'kamon'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            familycrest.push(x);
        });

        const lame: PartsMesh[] = [];
        [
            ['rame1', 'rame'],
            ['rame2', 'rame'],
            ['rame3', 'rame'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            lame.push(x);
        });

        const deco: PartsMesh[] = [];
        [
            ['deco1', 'deco1'],
            ['deco2', 'deco2'],
            ['deco3', 'deco3'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            deco.push(x);
        });

        const { canvas, context, tex } = setupCarving();

        const parts = {
            shaft,
            haneFront,
            haneBack,
            threads,
            kebikis,
            tenkyuHazu,
            hukumiHazu,
            shortYajiri,
            longYajiri,
            hazumakiFix,
            carving,
            familycrest,
            hanaka,
            lame,
            deco,
            canvas,
            context,
            tex,
        };

        return new KintekiOrdermadeArrowModel(model, parts);
    } catch (error) {
        console.error(error);
    }

    return new KintekiOrdermadeArrowModel(model);
};

export const buildEnteki = (model: Group, enableRestore: boolean): EntekiOrdermadeArrowModel => {
    try {
        const shaft = partsMeshBuild(model, 'shaft', 'shaft', enableRestore) as PartsMeshs;
        const threads: PartsMeshs[] = [];
        [['ito1', 'thread'], ['ito2', 'thread'], ['ito3', 'thread']].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            threads.push(x);
        });
        const kebikis: PartsMeshs[] = [];
        [
            ['ke01', 'kebiki'],
            ['ke02', 'kebiki'],
            ['ke03', 'kebiki'],
            ['ke04', 'kebiki'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            kebikis.push(x);
        });

        // default show: hukumi-hazu
        const tenkyuHazu = partsMeshBuild(model, 'yahazu1', 'yahazu', enableRestore) as PartsMesh;
        const hukumiHazu = partsMeshBuild(model, 'yahazu2', 'yahazu', enableRestore) as PartsMesh;
        // default show: long-yajiri
        const shortYajiri = partsMeshBuild(model, 'yaziri1', 'yaziri', enableRestore) as PartsMesh;
        const longYajiri = partsMeshBuild(model, 'yaziri2', 'yaziri', enableRestore) as PartsMesh;
        const hazumakiFix = partsMeshBuild(model, 'hazumaki', 'hazumaki', enableRestore) as PartsMesh;
        const carving = partsMeshBuild(model, 'kokuin', 'kokuin', enableRestore) as PartsMesh;
        const hanaka = partsMeshBuild(model, 'hatyu', 'hatyu', enableRestore) as PartsMesh;

        const hane1: Array<[PartsMeshs, PartsMeshs]> = [];
        [
            ['hane11', 'haneo', 'haneu'],
            ['hane12', 'haneo', 'haneu'],
            ['hane13', 'haneo', 'haneu'],
        ].forEach(([mesh, matfront, matback]) => {
            const front = partsMeshBuild(model, mesh, matfront, enableRestore) as PartsMeshs;
            const back = partsMeshBuild(model, mesh, matback, enableRestore) as PartsMeshs;
            hane1.push([front, back]);
        });

        const hane2: Array<[PartsMeshs, PartsMeshs]> = [];
        [
            ['hane21', 'haneo', 'haneu'],
            ['hane22', 'haneo', 'haneu'],
            ['hane23', 'haneo', 'haneu'],
        ].forEach(([mesh, matfront, matback]) => {
            const front = partsMeshBuild(model, mesh, matfront, enableRestore) as PartsMeshs;
            const back = partsMeshBuild(model, mesh, matback, enableRestore) as PartsMeshs;
            hane2.push([front, back]);
        });

        const hane3: Array<[PartsMeshs, PartsMeshs]> = [];
        [
            ['hane31', 'haneo', 'haneu'],
            ['hane32', 'haneo', 'haneu'],
            ['hane33', 'haneo', 'haneu'],
        ].forEach(([mesh, matfront, matback]) => {
            const front = partsMeshBuild(model, mesh, matfront, enableRestore) as PartsMeshs;
            const back = partsMeshBuild(model, mesh, matback, enableRestore) as PartsMeshs;
            hane3.push([front, back]);
        });

        const lame: PartsMesh[] = [];
        [
            ['rame1', 'rame'],
            ['rame2', 'rame'],
            ['rame3', 'rame'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            lame.push(x);
        });

        const deco: PartsMesh[] = [];
        [
            ['deco1', 'deco1'],
            ['deco2', 'deco2'],
            ['deco3', 'deco3'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            deco.push(x);
        });

        const { canvas, context, tex } = setupCarving();

        const parts = {
            shaft,
            hane1,
            hane2,
            hane3,
            threads,
            kebikis,
            tenkyuHazu,
            hukumiHazu,
            shortYajiri,
            longYajiri,
            hazumakiFix,
            carving,
            hanaka,
            lame,
            deco,
            canvas,
            context,
            tex,
        };

        return new EntekiOrdermadeArrowModel(model, parts);
    } catch (error) {
        console.error(error);
    }

    return new EntekiOrdermadeArrowModel(model);
};

export const buildMakiwara = (
    model: Group,
    enableRestore: boolean,
): MakiwaraOrdermadeArrowModel => {
    try {
        const shaft = partsMeshBuild(model, 'shaft', 'shaft', enableRestore) as PartsMeshs;

        const threads: PartsMeshs[] = [];
        [
            ['ito1', 'thread'],
            ['ito2', 'thread'],
            ['ito3', 'thread'],
            ['ito4', 'thread'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            threads.push(x);
        });

        const kebikis: PartsMeshs[] = [];
        [
            ['ke01', 'kebiki'],
            ['ke02', 'kebiki'],
            ['ke03', 'kebiki'],
            ['ke04', 'kebiki'],
            ['ke05', 'kebiki'],
            ['ke06', 'kebiki'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMeshs;
            kebikis.push(x);
        });

        // default show: hukumi-hazu
        const tenkyuHazu = partsMeshBuild(model, 'yahazu1', 'yahazu', enableRestore) as PartsMesh;
        const hukumiHazu = partsMeshBuild(model, 'yahazu2', 'yahazu', enableRestore) as PartsMesh;

        const yajiri = partsMeshBuild(model, 'yaziri1', 'yaziri', enableRestore) as PartsMesh;

        const hazumakiFix = partsMeshBuild(model, 'hazumaki', 'hazumaki', enableRestore) as PartsMesh;
        const carving = partsMeshBuild(model, 'kokuin', 'kokuin', enableRestore) as PartsMesh;
        const hanaka = partsMeshBuild(model, 'hatyu', 'hatyu', enableRestore) as PartsMesh;

        const hane1: Array<[PartsMeshs, PartsMeshs]> = [];
        [
            ['hane11', 'haneo', 'haneu'],
            ['hane12', 'haneo', 'haneu'],
            ['hane13', 'haneo', 'haneu'],
        ].forEach(([mesh, matfront, matback]) => {
            const front = partsMeshBuild(model, mesh, matfront, enableRestore) as PartsMeshs;
            const back = partsMeshBuild(model, mesh, matback, enableRestore) as PartsMeshs;
            hane1.push([front, back]);
        });

        const hane3: Array<[PartsMeshs, PartsMeshs]> = [];
        [
            ['hane31', 'haneo', 'haneu'],
            ['hane32', 'haneo', 'haneu'],
            ['hane33', 'haneo', 'haneu'],
        ].forEach(([mesh, matfront, matback]) => {
            const front = partsMeshBuild(model, mesh, matfront, enableRestore) as PartsMeshs;
            const back = partsMeshBuild(model, mesh, matback, enableRestore) as PartsMeshs;
            hane3.push([front, back]);
        });

        const familycrest: PartsMesh[] = [];

        const lame: PartsMesh[] = [];
        [
            ['rame1', 'rame'],
            ['rame2', 'rame'],
            ['rame3', 'rame'],
            ['rame4', 'rame'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            lame.push(x);
        });

        const deco: PartsMesh[] = [];
        [
            ['deco1', 'deco1'],
            ['deco2', 'deco2'],
            ['deco3', 'deco3'],
        ].forEach(([mesh, mat]) => {
            const x = partsMeshBuild(model, mesh, mat, enableRestore) as PartsMesh;
            deco.push(x);
        });

        const { canvas, context, tex } = setupCarving();

        const parts = {
            shaft,
            hane1,
            hane3,
            threads,
            kebikis,
            tenkyuHazu,
            hukumiHazu,
            yajiri,
            hazumakiFix,
            carving,
            familycrest,
            hanaka,
            lame,
            deco,
            canvas,
            context,
            tex,
        };

        return new MakiwaraOrdermadeArrowModel(model, parts);
    } catch (error) {
        console.error(error);
    }

    return new MakiwaraOrdermadeArrowModel(model);
};
